import React from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, View } from 'react-native'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import CopyIcon from '@common/assets/svg/CopyIcon'
import RightArrowIcon from '@common/assets/svg/RightArrowIcon'
import AccountKeyIcons from '@common/components/AccountKeyIcons'
import Avatar from '@common/components/Avatar'
import Text from '@common/components/Text'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexboxStyles from '@common/styles/utils/flexbox'
import { setStringAsync } from '@common/utils/clipboard'
import useHover, { AnimatedPressable, useCustomHover } from '@web/hooks/useHover'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

import useRailgunContext from '@web/hooks/useRailgunContext'
import useAppMode from '@web/hooks/useAppMode'

import { NEUTRAL_BACKGROUND_HOVERED } from '../../screens/styles'
import getStyles from './styles'

type Props = {
  /**
   * Where the button is painted.
   * - `banner` (default): the dashboard's mode gradient banner — a translucent
   *   dark pill with white-ish label/address, which is what that gradient needs.
   * - `surface`: a plain header surface (the Swap/Send/Shield tab header), where
   *   the banner's inverted palette would render white text on a light header.
   *   Uses the regular text tokens and a subtle tertiary-background pill.
   */
  variant?: 'banner' | 'surface'
}

const AccountButton = ({ variant = 'banner' }: Props) => {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { navigate } = useNavigation()
  const { theme, styles, themeType } = useTheme(getStyles)

  const { mode } = useAppMode()
  const { currentRailgunWallet } = useRailgunContext()

  const { account } = useSelectedAccountControllerState()
  const [bindAddressAnim, addressAnimStyle] = useHover({
    preset: 'opacity'
  })
  const [bindAccountBtnAnim, accountBtnAnimStyle, isAccountBtnHovered] = useCustomHover({
    property: 'left',
    values: {
      from: 0,
      to: 4
    }
  })

  if (!account) return null

  const onBanner = variant === 'banner'
  const labelColor = onBanner
    ? themeType === THEME_TYPES.DARK
      ? theme.primaryBackgroundInverted
      : theme.primaryBackground
    : theme.primaryText
  const addressColor = onBanner
    ? themeType === THEME_TYPES.DARK
      ? theme.secondaryBackgroundInverted
      : theme.secondaryBackground
    : theme.secondaryText

  const addressToDisplay =
    mode === 'private' && currentRailgunWallet?.railgunAddress
      ? currentRailgunWallet.railgunAddress
      : account.addr

  const handleCopyText = async () => {
    try {
      await setStringAsync(addressToDisplay)
      addToast(t('Copied address to clipboard!') as string, { timeout: 2500 })
    } catch {
      addToast(t('Failed to copy address to clipboard!') as string, {
        timeout: 2500,
        type: 'error'
      })
    }
  }

  // `surface` stacks the address UNDER the name (the layout the mobile-web
  // header uses) instead of trailing it on one line: the compact tab header only
  // has ~40px of height to spend, and one long line pushed the wordmark around.
  // The avatar sits outside both press targets so the name row (-> account
  // select) and the address row (-> copy) stay separate, non-nested Pressables.
  if (!onBanner) {
    return (
      <View style={[flexboxStyles.directionRow, flexboxStyles.alignCenter]}>
        <Avatar
          pfp={account.preferences.pfp}
          address={account.addr}
          size={32}
          borderRadius={16}
          isSmart={isSmartAccount(account)}
        />
        <View style={styles.accountStack}>
          <AnimatedPressable
            testID="account-select-btn"
            style={[styles.accountStackRow, flexboxStyles.flex1]}
            onPress={() => navigate(WEB_ROUTES.accountSelect)}
            {...bindAccountBtnAnim}
          >
            <>
              <Text
                numberOfLines={1}
                weight="semiBold"
                color={labelColor}
                fontSize={14}
                style={spacings.mrMi}
              >
                {account.preferences.label}
              </Text>
              <AccountKeyIcons isExtended={false} account={account} />
              <Animated.View style={[spacings.mlMi, accountBtnAnimStyle]}>
                <RightArrowIcon
                  style={styles.accountButtonRightIcon}
                  width={12}
                  color={labelColor}
                />
              </Animated.View>
            </>
          </AnimatedPressable>
          <AnimatedPressable
            style={[styles.accountStackRow, addressAnimStyle]}
            onPress={handleCopyText}
            {...bindAddressAnim}
          >
            <>
              <Text color={addressColor} style={spacings.mrMi} weight="medium" fontSize={12}>
                {shortenAddress(addressToDisplay, 13)}
              </Text>
              <CopyIcon width={14} height={14} color={addressColor} />
            </>
          </AnimatedPressable>
        </View>
      </View>
    )
  }

  return (
    <View style={[flexboxStyles.directionRow, flexboxStyles.alignCenter]}>
      <AnimatedPressable
        testID="account-select-btn"
        style={[
          styles.accountButton,
          {
            backgroundColor: onBanner
              ? NEUTRAL_BACKGROUND_HOVERED
              : isAccountBtnHovered
                ? theme.tertiaryBackground
                : 'transparent'
          }
        ]}
        onPress={() => navigate(WEB_ROUTES.accountSelect)}
        {...bindAccountBtnAnim}
      >
        <>
          <View style={styles.accountButtonInfo}>
            <Avatar
              pfp={account.preferences.pfp}
              address={account.addr}
              size={32}
              borderRadius={16}
              isSmart={isSmartAccount(account)}
            />
            <Text
              numberOfLines={1}
              weight="semiBold"
              style={[spacings.mlTy, spacings.mrTy]}
              color={labelColor}
              fontSize={14}
            >
              {account.preferences.label}
            </Text>

            <AccountKeyIcons isExtended={false} account={account} />
          </View>
          <Animated.View style={accountBtnAnimStyle}>
            <RightArrowIcon style={styles.accountButtonRightIcon} width={12} color={labelColor} />
          </Animated.View>
        </>
      </AnimatedPressable>
      <AnimatedPressable
        style={[flexboxStyles.directionRow, flexboxStyles.alignCenter, addressAnimStyle]}
        onPress={handleCopyText}
        {...bindAddressAnim}
      >
        <Text color={addressColor} style={spacings.mrMi} weight="medium" fontSize={14}>
          ({shortenAddress(addressToDisplay, 13)})
        </Text>
        <CopyIcon width={20} height={20} color={addressColor} />
      </AnimatedPressable>
    </View>
  )
}

export default React.memo(AccountButton)
